import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Mail, Smartphone, Monitor, Clock, AlertTriangle } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { env } from '@/lib/env';
import { useMe } from '@/lib/brief';
import { cn } from '@/lib/cn';

/**
 * Lead-inbox surface for clinic owners (#136).
 *
 * /api/leads is RODO transit-only — we DON'T store message body or
 * PII. Each form submission writes an audit row (timestamp, source
 * page, UA family, email delivery state, message id) and proxies the
 * actual lead via OUTBOUND_EMAIL straight to the clinic's
 * contact_email. So this page is "how many leads came in, did they
 * deliver, what page did they come from" — not a chat-style inbox.
 *
 * Owner reads the actual messages in their email client (Gmail,
 * Outlook). This page is the supervision view: counts + missed
 * deliveries warning + source-page traffic.
 */
export const Route = createFileRoute('/_app/messages')({
  staticData: { crumb: 'Wiadomości' },
  component: MessagesPage,
});

interface LeadRow {
  id: string;
  ts: number;
  source_page: string;
  ua_family: string | null;
  delivery_state: 'sent' | 'bounced' | 'failed' | 'unknown';
  email_msg_id: string;
}

interface LeadsResponse {
  leads: LeadRow[];
  counts: { total: number; last_7d: number };
}

function MessagesPage() {
  const me = useMe();
  const contactEmail =
    (me.data?.tenant as { contact_email?: string } | undefined)?.contact_email ?? null;

  const leads = useQuery({
    queryKey: ['admin', 'leads'],
    queryFn: async (): Promise<LeadsResponse> => {
      const res = await fetch(`${env.apiBaseUrl}/api/admin/leads`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`leads_${res.status}`);
      return (await res.json()) as LeadsResponse;
    },
    refetchInterval: 60_000,
  });

  const rows = leads.data?.leads ?? [];
  const last7d = leads.data?.counts.last_7d ?? 0;
  const total = leads.data?.counts.total ?? 0;
  const bouncedRecent = rows
    .filter((r) => r.ts >= Math.floor(Date.now() / 1000) - 7 * 86400)
    .filter((r) => r.delivery_state === 'bounced' || r.delivery_state === 'failed');

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Wiadomości
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Każde wypełnienie formularza /kontakt na Twojej stronie trafia
          bezpośrednio na Twój e-mail. Tu zobaczysz ile ich przyszło i czy
          dostarczenie się powiodło.
        </p>
      </header>

      <div className="grid gap-3 md:grid-cols-3">
        <KpiCard label="Ostatnie 7 dni" value={`${last7d}`} hint="wiadomości z formularza" />
        <KpiCard
          label="Razem (ostatnie 50)"
          value={`${total}`}
          hint="historia widocznych wpisów"
        />
        <KpiCard
          label="Wysyłane na"
          value={contactEmail ?? '—'}
          hint="zmień w Treści strony → Dane podstawowe"
          mono
        />
      </div>

      {bouncedRecent.length > 0 ? (
        <Card className="ring-2 ring-amber-500/40">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="size-5 shrink-0 text-amber-500" aria-hidden="true" />
            <div>
              <p className="font-medium text-text">
                {bouncedRecent.length} wiadomości nie dotarły w ostatnich 7 dniach
              </p>
              <p className="mt-1 text-xs text-text-muted">
                Sprawdź czy adres <span className="font-mono">{contactEmail}</span> jest
                aktywny i nie odbija wiadomości. Skontaktuj się z nami, jeśli problem
                się powtarza.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Historia (audyt RODO)</CardTitle>
          <CardDescription>
            Zachowujemy tylko ślad audytowy — timestamp, źródłowa strona, kategoria
            urządzenia, status dostarczenia. Bez treści wiadomości i bez danych
            osobowych pacjentów (RODO art. 28).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {leads.isPending ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState contactEmail={contactEmail} />
          ) : (
            <ul role="list" className="divide-y divide-border">
              {rows.map((r) => (
                <LeadRowItem key={r.id} row={r} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-text-subtle">
        Treść wiadomości od pacjentów znajdziesz w swojej skrzynce pocztowej (
        <span className="font-mono">{contactEmail ?? 'adres nie ustawiony'}</span>). Tu
        widzisz tylko statystyki dostarczenia.
      </p>
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  mono,
}: {
  label: string;
  value: string;
  hint: string;
  mono?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-text-subtle">{label}</div>
        <p
          className={cn(
            'mt-2 font-display text-2xl font-semibold text-text',
            mono ? 'truncate font-mono text-base' : 'tabular-nums',
          )}
        >
          {value}
        </p>
        <p className="mt-1 text-xs text-text-muted">{hint}</p>
      </CardContent>
    </Card>
  );
}

function LeadRowItem({ row }: { row: LeadRow }) {
  const ts = new Date(row.ts * 1000);
  const tsLabel = ts.toLocaleString('pl-PL', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const isMobile = (row.ua_family ?? '').toLowerCase().includes('mobile');
  const DeviceIcon = isMobile ? Smartphone : Monitor;
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
      <span className="inline-flex items-center gap-1.5 text-xs text-text-subtle tabular-nums">
        <Clock className="size-3" aria-hidden="true" />
        {tsLabel}
      </span>
      <span className="inline-flex min-w-0 items-center gap-1.5 truncate text-text-muted">
        <Mail className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate font-mono">{row.source_page}</span>
      </span>
      <span className="inline-flex items-center gap-1.5 text-xs text-text-subtle">
        <DeviceIcon className="size-3" aria-hidden="true" />
        {row.ua_family ?? 'unknown'}
      </span>
      <span className="ml-auto">
        <DeliveryBadge state={row.delivery_state} />
      </span>
    </li>
  );
}

function DeliveryBadge({ state }: { state: LeadRow['delivery_state'] }) {
  const meta: Record<
    LeadRow['delivery_state'],
    { variant: 'success' | 'destructive' | 'soft'; label: string }
  > = {
    sent: { variant: 'success', label: 'dostarczone' },
    bounced: { variant: 'destructive', label: 'odbite' },
    failed: { variant: 'destructive', label: 'błąd' },
    unknown: { variant: 'soft', label: 'nieznane' },
  };
  const m = meta[state];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

function EmptyState({ contactEmail }: { contactEmail: string | null }) {
  return (
    <div className="p-6 text-sm text-text-muted">
      <p>
        Brak wiadomości jeszcze. Po publikacji strony formularz /kontakt zacznie
        kierować wiadomości na{' '}
        {contactEmail ? (
          <span className="font-mono text-text">{contactEmail}</span>
        ) : (
          <Link to={'/brief' as never} className="text-accent hover:underline">
            adres ustawiony w Dane podstawowe
          </Link>
        )}
        .
      </p>
      <p className="mt-2 text-xs text-text-subtle">
        Widoczne jest do 50 ostatnich wpisów. Starsze są zachowywane w audycie RODO
        przez 90 dni, dostępne na żądanie.
      </p>
    </div>
  );
}
